import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, Grid, IconButton, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { Close, Info, MoreVert } from "@mui/icons-material";
import { toast } from "react-toastify";
import { translate } from "../../../../../translate/translate";
import { NpiActivity, NpiActivityGroup, NpiActivityStatus, NpiResource } from "../types/npi-project.types";
import TaskIcon from '@mui/icons-material/Task';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { Trans } from "react-i18next";
import { ATTENTION_STYLE, CRITICAL_DELAY_DAYS, STATUS_STYLES, StatusStyle } from "../constants/dashboard.constants";

const AUTO_SAVE_DELAY_MS = 800;

interface NpiActivityGroupProps {
    group: NpiActivityGroup;
    onDurationChange: (activityId: number, duration: number) => void;
    onStatusChange: (activityId: number, status: NpiActivityStatus) => void;
}

const RESOURCE_LABELS: Record<NpiResource, string> = {
    China: "CHINA",
    Manaus: "MANAUS",
    "Sao Paulo": "SÃO PAULO",
};

const statusStyle = (activity: NpiActivity): StatusStyle => {
    const delayDays = activity.delayDays ?? 0;
    const isLate = activity.status === "atrasado" || (activity.status === "concluido" && delayDays > 0);
    if (!isLate) {
        return STATUS_STYLES[activity.status];
    }
    return delayDays >= CRITICAL_DELAY_DAYS ? STATUS_STYLES.atrasado : ATTENTION_STYLE;
};

const activityStatusLabel = (activity: NpiActivity): string => {
    const delayDays = activity.delayDays ?? 0;
    if (activity.status === "atrasado" && delayDays > 0) {
        return translate.t("delayed_days_ago", { count: delayDays });
    }
    if (activity.status === "concluido" && delayDays > 0) {
        return translate.t("status_completed_late");
    }
    return statusLabel(activity.status);
};

const statusLabel = (status: NpiActivityStatus): string => {
    const labels: Record<NpiActivityStatus, string> = {
        concluido: translate.t("status_completed"),
        em_andamento: translate.t("status_in_progress"),
        atrasado: translate.t("status_delayed"),
        nao_iniciada: translate.t("status_not_started"),
        nao_sera_executada: translate.t("status_not_executed"),
    };
    return labels[status];
};

const groupTitleSx = {
    fontWeight: 800,
    fontSize: "15px",
};

const groupDateSx = {
    fontWeight: 400,
    fontSize: "11.5px",
};

const groupPercentSx = {
    fontWeight: 500,
    fontSize: "12px",
};

const columnHeaderSx = {
    fontWeight: 700,
    fontSize: "10px",
    whiteSpace: "nowrap",
};

const NpiActivityGroupComponent: React.FC<NpiActivityGroupProps> = ({ group, onDurationChange, onStatusChange }) => {
    const [durationDrafts, setDurationDrafts] = useState<Record<number, string>>({});
    const [pendingStatusChange, setPendingStatusChange] = useState<{ activity: NpiActivity; status: NpiActivityStatus; label: string; messageKey: string } | null>(null);
    const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);

    const autoSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    useEffect(() => () => Object.values(autoSaveTimers.current).forEach(clearTimeout), []);

    const cancelAutoSave = (timerKey: string) => {
        clearTimeout(autoSaveTimers.current[timerKey]);
        delete autoSaveTimers.current[timerKey];
    };

    const scheduleAutoSave = (timerKey: string, save: () => void) => {
        cancelAutoSave(timerKey);
        autoSaveTimers.current[timerKey] = setTimeout(() => {
            delete autoSaveTimers.current[timerKey];
            save();
        }, AUTO_SAVE_DELAY_MS);
    };

    const renderExecutionDateField = (activity: NpiActivity, value: string | null) => (
        <TextField
            size="small"
            fullWidth
            type="date"
            disabled={activity.status === "nao_sera_executada"}
            value={value ?? ""}
            slotProps={{ htmlInput: { readOnly: true, tabIndex: -1 } }}
            sx={{ pointerEvents: "none" }}
        />
    );

    const durationKey = (activityId: number) => `${activityId}-duration`;

    const commitDuration = (activity: NpiActivity, draft: string | undefined) => {
        cancelAutoSave(durationKey(activity.id));
        if (draft === undefined) {
            return;
        }
        const parsed = draft.trim() === "" ? NaN : Number(draft);
        setDurationDrafts((current) => {
            const next = { ...current };
            delete next[activity.id];
            return next;
        });
        if (Number.isNaN(parsed) || parsed === activity.duration) {
            return;
        }
        if (parsed < 1) {
            toast.error(translate.t("duration_must_be_positive"));
            return;
        }
        onDurationChange(activity.id, parsed);
    };

    const renderActivityRow = (activity: NpiActivity, depth: number) => (
        <TableRow key={`activity-${activity.id}`}>
            <TableCell>{activity.number}</TableCell>
            <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                <Box sx={{ pl: depth * 2 }}>
                    {activity.name}
                    {activity.dependencies && activity.dependencies.length > 0 && (
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                            {activity.dependencies.map((dep) => (
                                <Tooltip key={dep.id} title={dep.name}>
                                    <Chip label={dep.number ?? dep.name} size="small" sx={{ height: 18, fontSize: "11px", color: "#4338CA", backgroundColor: "#C7D2FE" }} />
                                </Tooltip>
                            ))}
                        </Box>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <Chip
                    sx={{
                        borderRadius: "6px",
                        maxWidth: "100%",
                        color: "#4f4f4f",
                        backgroundColor: "#F2F3F6",
                        borderColor: "#cfd1d2",
                    }}
                    label={RESOURCE_LABELS[activity.resource]}
                    size="small"
                    variant="outlined"
                />
            </TableCell>
            <TableCell>
                <TextField
                    size="small"
                    type="number"
                    disabled={activity.status === "nao_sera_executada" || activity.status === "concluido"}
                    value={durationDrafts[activity.id] ?? activity.duration}
                    onChange={(event) => {
                        const value = event.target.value;
                        setDurationDrafts((current) => ({ ...current, [activity.id]: value }));
                        if (value.trim() !== "") {
                            scheduleAutoSave(durationKey(activity.id), () => commitDuration(activity, value));
                        }
                    }}
                    onBlur={() => commitDuration(activity, durationDrafts[activity.id])}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            (event.target as HTMLInputElement).blur();
                        }
                    }}
                    slotProps={{ htmlInput: { min: 1 } }}
                    sx={{ width: 70, borderRadius: "8px" }}
                />
            </TableCell>
            <TableCell sx={activity.status === "nao_sera_executada" ? { color: "text.disabled" } : undefined}>
                {activity.baselineStart}
            </TableCell>
            <TableCell sx={activity.status === "nao_sera_executada" ? { color: "text.disabled" } : undefined}>
                {activity.baselineEnd}
            </TableCell>
            <TableCell>
                {renderExecutionDateField(activity, activity.executionStart)}
            </TableCell>
            <TableCell>
                {renderExecutionDateField(activity, activity.executionEnd)}
            </TableCell>
            <TableCell>
                <Tooltip title={activity.delayDays ? translate.t(activity.status === "concluido" ? "completed_late_days" : "delayed_days_ago", { count: activity.delayDays }) : ""}>
                    <Chip
                        label={activityStatusLabel(activity)}
                        size="small"
                        sx={{
                            color: statusStyle(activity).color,
                            backgroundColor: statusStyle(activity).background,
                            border: statusStyle(activity).border,
                            fontWeight: "bold",
                            borderRadius: "6px",
                            width: "100%",
                            maxWidth: 150,
                            height: "auto",
                            minHeight: 24,
                            justifyContent: "center",
                            "& .MuiChip-label": {
                                width: "100%",
                                textAlign: "center",
                                whiteSpace: "normal",
                                lineHeight: 1.2,
                                py: 0.25,
                            },
                        }}
                    />
                </Tooltip>
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title={translate.t("complete_activity")}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={activity.status === "concluido" || activity.status === "nao_sera_executada"}
                            onClick={() => {
                                setPendingStatusChange({ activity, status: "concluido", label: translate.t("complete_activity"), messageKey: "confirm_status_change" });
                                setIsStatusDialogOpen(true);
                            }}>
                            <TaskIcon sx={{ fontSize: "22px" }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={activity.status === "nao_sera_executada" ? translate.t("undo_not_executed") : translate.t("mark_not_executed")}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={activity.status === "concluido"}
                            onClick={() => {
                                setPendingStatusChange(
                                    activity.status === "nao_sera_executada"
                                        ? { activity, status: "nao_iniciada", label: translate.t("undo_not_executed"), messageKey: "confirm_undo_not_executed" }
                                        : { activity, status: "nao_sera_executada", label: translate.t("mark_not_executed"), messageKey: "confirm_mark_not_executed" }
                                );
                                setIsStatusDialogOpen(true);
                            }}>
                            <EventBusyIcon
                                sx={{ fontSize: "22px", color: activity.status === "nao_sera_executada" ? "primary.main" : undefined }}
                            />
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
        </TableRow>
    );

    const renderSubgroupHeader = (subgroup: NpiActivityGroup, depth: number) => (
        <TableRow key={`group-${subgroup.id}`} sx={{ backgroundColor: depth === 1 ? "#EAF2FB" : "#F1F6FB" }}>
            <TableCell sx={{ fontWeight: 700, color: "#475569" }}>{subgroup.number}</TableCell>
            <TableCell colSpan={9} sx={{ py: 0.75 }}>
                <Box sx={{ pl: depth * 2, display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "14px", color: "#1F2937" }}>
                        {subgroup.name}
                    </Typography>
                    {(subgroup.startDate || subgroup.endDate) && (
                        <Typography sx={{ fontWeight: 500, fontSize: "11.5px", color: "#64748B" }}>
                            {subgroup.startDate}{" -> "}{subgroup.endDate}
                        </Typography>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderGroupRows = (currentGroup: NpiActivityGroup, depth: number): React.ReactNode[] => [
        ...(depth > 0 ? [renderSubgroupHeader(currentGroup, depth)] : []),
        ...currentGroup.tasks.map((activity) => renderActivityRow(activity, depth)),
        ...currentGroup.children.flatMap((child) => renderGroupRows(child, depth + 1)),
    ];

    return (
        <Box sx={{ border: "1px solid #E2E8F0", borderRadius: "16px", mb: 2, overflow: "hidden", backgroundColor: "#FFFFFF" }}>
            <Grid container alignItems="center" spacing={2} sx={{ background: "#F2F3F6", px: 2, py: 1.5 }}>
                <Grid sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={groupTitleSx} color="text.primary">{group.number}</Typography>
                    <Typography sx={groupTitleSx} color="text.primary">{group.name}</Typography>
                    <Typography sx={groupDateSx} color="text.secondary">
                        {group.startDate} → {group.endDate}
                    </Typography>
                </Grid>
                <Grid sx={{ flexGrow: 1 }} />
                <Grid sx={{ minWidth: 160 }}>
                    <LinearProgress
                        variant="determinate"
                        value={group.progress}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#E5E7EB",
                            "& .MuiLinearProgress-bar": { backgroundColor: "#22C55E" },
                        }}
                    />
                </Grid>
                <Grid>
                    <Typography sx={groupPercentSx} color="text.primary">{group.progress}%</Typography>
                </Grid>
                <Grid>
                    {/* <IconButton size="small">
                        <MoreVert />
                    </IconButton> */}
                </Grid>
            </Grid>

            <Table size="small" className="npi-activity-table" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...columnHeaderSx, width: "2%" }}>ID</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "17%" }}>{translate.t("activity")}</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "8%" }}>{translate.t("resource")}</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "7%" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                {translate.t("duration")}
                                <Tooltip title={translate.t("duration_change_warning")}>
                                    <Info sx={{ fontSize: "14px", color: "#6B7280", cursor: "help" }} />
                                </Tooltip>
                            </Box>
                        </TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "8%" }}>{translate.t("baseline_start")}</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "7%" }}>{translate.t("baseline_end")}</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "13%" }}>{translate.t("execution_start")}</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "13%" }}>{translate.t("execution_end")}</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "10%" }}>{translate.t("status")}</TableCell>
                        <TableCell sx={{ ...columnHeaderSx, width: "6%" }}>{translate.t("actions")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderGroupRows(group, 0)}
                </TableBody>
            </Table>

            <Dialog open={isStatusDialogOpen} onClose={() => setIsStatusDialogOpen(false)}>
                <Grid p={2} container justifyContent="space-between" sx={{ backgroundColor: "primary.main" }}>
                    <Grid>
                        <Typography sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}>
                            {pendingStatusChange?.label}
                        </Typography>
                    </Grid>
                    <Grid sx={{ cursor: "pointer", color: "#ffffff" }}>
                        <Close onClick={() => setIsStatusDialogOpen(false)} />
                    </Grid>
                </Grid>
                <DialogContent>
                    <Grid container flexDirection="column" gap={2}>
                        <Typography>
                            <Trans
                                i18nKey={pendingStatusChange?.messageKey}
                                components={{ bold: <strong /> }}
                            />
                        </Typography>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="flex-end" gap={1} p={2}>
                        <Button color="secondary" variant="outlined" onClick={() => setIsStatusDialogOpen(false)}>
                            {translate.t("cancel")}
                        </Button>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                                if (!pendingStatusChange) {
                                    return;
                                }
                                onStatusChange(pendingStatusChange.activity.id, pendingStatusChange.status);
                                setIsStatusDialogOpen(false);
                            }}>
                            {translate.t("confirm")}
                        </Button>
                    </Grid>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NpiActivityGroupComponent;
